import { createGradingScaleSchema } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  HiddenFieldErrors,
  Notice,
  errorMessage,
  primaryButton,
  secondaryButton,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';

export interface Band {
  grade: string;
  minPercent: number;
  gradePoint: number | null;
  remark: string | null;
  isPass: boolean;
}
export interface Scale {
  id: string;
  name: string;
  bands: Band[];
  examCount: number;
}

interface BandDraft {
  grade: string;
  minPercent: string;
  gradePoint: string;
  isPass: boolean;
}
const blankBand = (): BandDraft => ({ grade: '', minPercent: '', gradePoint: '', isPass: true });

export function GradingTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [bands, setBands] = useState<BandDraft[]>([blankBand()]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  const scales = useQuery({
    queryKey: ['grading', schoolId],
    queryFn: () => api<Scale[]>(`/schools/${schoolId}/grading-scales`),
  });
  const create = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/grading-scales`, { method: 'POST', body }),
    onSuccess: () => {
      setName('');
      setBands([blankBand()]);
      setMessage(null);
      void queryClient.invalidateQueries({ queryKey: ['grading', schoolId] });
    },
    onError: (e) => setMessage(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/grading-scales/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['grading', schoolId] }),
    onError: (e) => setMessage(errorMessage(e)),
  });

  const setBand = (index: number, patch: Partial<BandDraft>) =>
    setBands(bands.map((b, i) => (i === index ? { ...b, ...patch } : b)));

  return (
    <section aria-label={t('exam.tab.grading')} className="space-y-4">
      {message && <Notice kind="error">{message}</Notice>}
      {scales.data?.length === 0 && <p className="text-slate-600">{t('grading.none')}</p>}
      <ul className="space-y-3">
        {scales.data?.map((scale) => (
          <li key={scale.id} className="rounded-lg border border-slate-200 bg-white p-4 text-sm">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-semibold">{scale.name}</h2>
              {can('grading.manage', schoolId) && scale.examCount === 0 && (
                <button
                  type="button"
                  className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                  aria-label={`${t('exam.paper.remove')}: ${scale.name}`}
                  onClick={() =>
                    window.confirm(t('common.confirmDelete', { name: scale.name })) &&
                    remove.mutate(scale.id)
                  }
                >
                  {t('exam.paper.remove')}
                </button>
              )}
            </div>
            <p className="mt-1 text-slate-700">
              {scale.bands
                .map((b) => `${b.grade} ≥ ${b.minPercent}%${b.isPass ? '' : ' ✗'}`)
                .join('  ·  ')}
            </p>
          </li>
        ))}
      </ul>

      {can('grading.manage', schoolId) && (
        <form
          noValidate
          aria-label={t('grading.create')}
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = createGradingScaleSchema.safeParse({
              name,
              bands: bands.map((b) => ({
                grade: b.grade,
                minPercent: b.minPercent === '' ? NaN : Number(b.minPercent),
                gradePoint: b.gradePoint === '' ? null : Number(b.gradePoint),
                isPass: b.isPass,
              })),
            });
            if (!parsed.success)
              return setErrors(
                Object.fromEntries(
                  parsed.error.issues.map((i) => [i.path.join('.') || 'bands', i.message]),
                ),
              );
            setErrors({});
            create.mutate(parsed.data);
          }}
          className="space-y-3 rounded-lg border border-slate-200 bg-white p-4"
        >
          <TextField
            label={t('grading.name')}
            value={name}
            error={errors.name}
            onChange={(e) => setName(e.target.value)}
          />
          {bands.map((band, index) => (
            <div key={index} className="grid items-end gap-3 sm:grid-cols-5">
              <TextField
                label={`${t('grading.grade')} ${index + 1}`}
                value={band.grade}
                error={errors[`bands.${index}.grade`]}
                onChange={(e) => setBand(index, { grade: e.target.value })}
              />
              <TextField
                label={`${t('grading.min')} ${index + 1}`}
                type="number"
                value={band.minPercent}
                error={errors[`bands.${index}.minPercent`]}
                onChange={(e) => setBand(index, { minPercent: e.target.value })}
              />
              <TextField
                label={`${t('grading.point')} ${index + 1}`}
                type="number"
                value={band.gradePoint}
                onChange={(e) => setBand(index, { gradePoint: e.target.value })}
              />
              <label className="flex items-center gap-2 pb-2 text-sm">
                <input
                  type="checkbox"
                  checked={band.isPass}
                  onChange={(e) => setBand(index, { isPass: e.target.checked })}
                />
                {t('grading.pass')}
              </label>
              <button
                type="button"
                className={secondaryButton}
                disabled={bands.length === 1}
                aria-label={`${t('grading.removeBand')} ${index + 1}`}
                onClick={() => setBands(bands.filter((_, i) => i !== index))}
              >
                {t('grading.removeBand')}
              </button>
            </div>
          ))}
          <HiddenFieldErrors
            errors={errors}
            visible={[
              'name',
              ...bands.flatMap((_, i) => [`bands.${i}.grade`, `bands.${i}.minPercent`]),
            ]}
          />
          <div className="flex gap-2">
            <button
              type="button"
              className={secondaryButton}
              onClick={() => setBands([...bands, blankBand()])}
            >
              {t('grading.addBand')}
            </button>
            <button type="submit" className={primaryButton} disabled={create.isPending}>
              {t('grading.create')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
